import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { GoogleMap, Marker, useLoadScript, InfoWindow } from "@react-google-maps/api";
import { CONFIG } from "../../../config";
import { Merchant, Transaction } from "../liveTransactionMapApi";
import TransactionOverlay from "./TransactionOverlay";
import { Typography, Tag, Space } from "antd";
import { EnvironmentOutlined, ShoppingOutlined } from "@ant-design/icons";
import styles from "./TransactionMap.module.css";

const { Text } = Typography;

interface TransactionMapProps {
  merchants: Map<string, Merchant>;
  transactions: Transaction[];
  newTransactions: Transaction[];
  onTransactionAnimate: (transaction: Transaction) => void;
  onTransactionComplete: (transactionId: string) => void;
}

/**
 * Google Maps component displaying merchants and animated transactions
 */
export default function TransactionMap({
  merchants,
  transactions,
  newTransactions,
  onTransactionAnimate,
  onTransactionComplete,
}: TransactionMapProps) {
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [activeAnimations, setActiveAnimations] = useState<
    Map<string, Transaction>
  >(new Map());
  const mapRef = useRef<google.maps.Map | null>(null);
  const animationQueueRef = useRef<Transaction[]>([]);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: CONFIG.GOOGLE_MAPS.API_KEY,
  });

  // Calculate map center - default to Amsterdam, adjust based on merchants
  const mapCenter = useMemo(() => {
    const merchantsArray = Array.from(merchants.values()).filter(
      (m) => m && typeof m.lat === "number" && typeof m.lng === "number" && 
             !isNaN(m.lat) && !isNaN(m.lng) &&
             m.lat >= -90 && m.lat <= 90 && m.lng >= -180 && m.lng <= 180
    );
    
    if (merchantsArray.length === 0) {
      return { lat: 52.3676, lng: 4.9041 }; // Amsterdam center
    }

    const avgLat =
      merchantsArray.reduce((sum, m) => sum + m.lat, 0) /
      merchantsArray.length;
    const avgLng =
      merchantsArray.reduce((sum, m) => sum + m.lng, 0) /
      merchantsArray.length;

    // Validate calculated center
    if (isNaN(avgLat) || isNaN(avgLng)) {
      return { lat: 52.3676, lng: 4.9041 }; // Fallback to Amsterdam
    }

    return { lat: avgLat, lng: avgLng };
  }, [merchants]);

  // Process new transactions and add to animation queue
  useEffect(() => {
    if (!newTransactions || newTransactions.length === 0) return;

    // Filter valid transactions
    const validTransactions = newTransactions.filter(
      (tx) => tx && tx._id && tx.businessId && tx.amount !== undefined
    );

    if (validTransactions.length === 0) return;

    // Add new transactions to queue
    animationQueueRef.current = [
      ...animationQueueRef.current,
      ...validTransactions,
    ];

    // Process queue if not already processing
    if (!animationIntervalRef.current) {
      processAnimationQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newTransactions]);

  // Process animation queue - distribute animations over time
  const processAnimationQueue = useCallback(() => {
    const MAX_CONCURRENT_ANIMATIONS = 30;
    const ANIMATION_INTERVAL = 200; // ms between starting animations

    const processNext = () => {
      if (animationQueueRef.current.length === 0) {
        if (animationIntervalRef.current) {
          clearInterval(animationIntervalRef.current);
          animationIntervalRef.current = null;
        }
        return;
      }

      // Check current active animations count
      setActiveAnimations((prev) => {
        // Check if we can start more animations
        if (prev.size >= MAX_CONCURRENT_ANIMATIONS) {
          return prev; // Wait for some to complete
        }

        // Get next transaction from queue
        const nextTransaction = animationQueueRef.current.shift();
        if (!nextTransaction) {
          return prev;
        }

        // Start animation
        const next = new Map(prev);
        next.set(nextTransaction._id, nextTransaction);
        onTransactionAnimate(nextTransaction);
        return next;
      });
    };

    // Process queue at intervals
    animationIntervalRef.current = setInterval(processNext, ANIMATION_INTERVAL);
  }, [onTransactionAnimate]);

  // Handle animation completion
  const handleAnimationComplete = useCallback(
    (transactionId: string) => {
      setActiveAnimations((prev) => {
        const next = new Map(prev);
        next.delete(transactionId);
        return next;
      });
      onTransactionComplete(transactionId);
    },
    [onTransactionComplete]
  );

  // Get marker color based on transaction volume (could be enhanced)
  const getMarkerColor = (merchantId: string): string => {
    const merchantTransactions = transactions.filter(
      (tx) => tx.businessId === merchantId
    );
    const count = merchantTransactions.length;

    if (count >= 10) return "#F44336"; // Red - High volume
    if (count >= 5) return "#FF9800"; // Orange - Medium volume
    return "#4CAF50"; // Green - Low volume
  };

  const handleMarkerClick = useCallback((merchant: Merchant) => {
    if (merchant && merchant.businessId) {
      setSelectedMerchant((prev) =>
        prev?.businessId === merchant.businessId ? null : merchant
      );
    }
  }, []);

  // Calculate merchant statistics
  const getMerchantStats = useCallback(
    (merchantId: string) => {
      const merchantTransactions = transactions.filter(
        (tx) => tx.businessId === merchantId
      );
      return {
        count: merchantTransactions.length,
        totalAmount: merchantTransactions.reduce((sum, tx) => sum + tx.amount, 0),
      };
    },
    [transactions]
  );

  // Format currency helper
  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    };
  }, []);

  if (loadError) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.errorMessage}>
          Error loading Google Maps. Please check your internet connection.
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.loadingMessage}>Loading map...</div>
      </div>
    );
  }

  const mapOptions: google.maps.MapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
    ],
  };

  return (
    <div className={styles.mapContainer} ref={mapContainerRef}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={mapCenter}
        zoom={merchants.size === 1 ? 15 : 8}
        options={mapOptions}
        onLoad={(map) => {
          mapRef.current = map;
        }}
      >
        {/* Merchant markers */}
        {Array.from(merchants.values())
          .filter(
            (m) =>
              m &&
              m.businessId &&
              typeof m.lat === "number" &&
              typeof m.lng === "number" &&
              !isNaN(m.lat) &&
              !isNaN(m.lng) &&
              m.lat >= -90 &&
              m.lat <= 90 &&
              m.lng >= -180 &&
              m.lng <= 180
          )
          .map((merchant) => (
            <Marker
              key={merchant.businessId}
              position={{ lat: merchant.lat, lng: merchant.lng }}
              onClick={() => handleMarkerClick(merchant)}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: getMarkerColor(merchant.businessId),
                fillOpacity: 0.8,
                strokeColor: "#fff",
                strokeWeight: 2,
              }}
            />
          ))}

        {/* Merchant InfoWindow */}
        {selectedMerchant && (
          <InfoWindow
            position={{ lat: selectedMerchant.lat, lng: selectedMerchant.lng }}
            onCloseClick={() => setSelectedMerchant(null)}
          >
            <div className={styles.infoWindowContent}>
              <Text strong style={{ fontSize: "16px", display: "block", marginBottom: "8px" }}>
                {selectedMerchant.businessTradeName}
              </Text>
              {selectedMerchant.businessName !== selectedMerchant.businessTradeName && (
                <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "8px" }}>
                  {selectedMerchant.businessName}
                </Text>
              )}
              {selectedMerchant.city && (
                <Space style={{ marginBottom: "8px" }}>
                  <Tag icon={<EnvironmentOutlined />} color="blue">
                    {selectedMerchant.city}
                  </Tag>
                </Space>
              )}
              {(() => {
                const stats = getMerchantStats(selectedMerchant.businessId);
                if (stats.count > 0) {
                  return (
                    <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f0f0f0" }}>
                      <Text type="secondary" strong style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
                        Today's Activity:
                      </Text>
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <div>
                          <ShoppingOutlined style={{ marginRight: "4px" }} />
                          <Text>Transactions: </Text>
                          <Text strong>{stats.count}</Text>
                        </div>
                        <div>
                          <Text>Total: </Text>
                          <Text strong style={{ color: "#3f8600" }}>
                            {formatCurrency(stats.totalAmount)}
                          </Text>
                        </div>
                      </Space>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </InfoWindow>
        )}

        {/* Transaction animations */}
        {Array.from(activeAnimations.values()).map((transaction) => {
          const merchant = merchants.get(transaction.businessId);
          if (!merchant) return null;

          return (
            <TransactionOverlay
              key={transaction._id}
              transaction={transaction}
              position={{ lat: merchant.lat, lng: merchant.lng }}
              onComplete={() => handleAnimationComplete(transaction._id)}
              mapContainerRef={mapContainerRef}
            />
          );
        })}
      </GoogleMap>
    </div>
  );
}
